import { Clock, ExternalLink, Edit3, Trash2, Moon, Navigation } from "lucide-react";
import { STATUSES, TIME_SLOTS, resolveIcon } from "../constants.js";
import { prettyHost } from "../utils/date.js";

const googleMapsDirectionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

export default function ItemCard({ item, onEdit, onDelete, onCycleStatus, onTitleClick, sleepy = false, categories }) {
  const cat = categories.find((c) => c.id === item.category) || categories[categories.length - 1] || { colour: "#64748b", icon: "sparkles" };
  const status = STATUSES.find((s) => s.id === item.status) || STATUSES[0];
  const slot = TIME_SLOTS.find((t) => t.id === item.timeSlot);
  const Icon = resolveIcon(cat.icon);

  const totalDays = item._totalDays || 1;
  const isMultiDay = totalDays > 1;
  const dayIndex = item._dayIndex || 0;
  const spanUnit = item.category === "accom" ? "Night" : "Day";
  const spanLabel = isMultiDay
    ? (item.category === "accom"
        ? (item._isFirst ? "Check in" : item._isLast ? "Check out" : `Night ${dayIndex} of ${totalDays - 1}`)
        : `${spanUnit} ${dayIndex + 1} of ${totalDays}`)
    : null;
  const rangeMarker = isMultiDay
    ? (item._isFirst ? "start" : item._isLast ? "end" : "middle")
    : null;

  if (sleepy) {
    return (
      <div
        className="group relative rounded-2xl overflow-hidden transition-all hover:shadow-lg"
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)",
          color: "#e2e8f0",
        }}
      >
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(1px 1px at 20% 30%, white, transparent), radial-gradient(1px 1px at 60% 70%, white, transparent), radial-gradient(1.5px 1.5px at 80% 20%, white, transparent), radial-gradient(1px 1px at 35% 80%, white, transparent), radial-gradient(1px 1px at 90% 60%, white, transparent)`,
          }}
        />
        <div className="absolute -right-6 -top-6 opacity-15">
          <Moon size={80} strokeWidth={1} />
        </div>
        {rangeMarker && (
          <div
            className="absolute left-0 top-0 bottom-0 w-1 flex flex-col items-center"
            style={{ background: rangeMarker === "middle" ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.5)" }}
          />
        )}
        <div className="relative px-4 py-3">
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
              <Icon size={14} className="text-slate-200" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <button onClick={() => onTitleClick?.(item)} className="font-medium leading-tight text-left hover:underline" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>{item.title}</button>
                {spanLabel && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-200/80 font-semibold">
                    {spanLabel}
                  </span>
                )}
                {slot && slot.id !== "anytime" && !spanLabel && (
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-0.5">
                    <Clock size={10} /> {slot.label}
                  </span>
                )}
              </div>
              {item.notes && <div className="text-xs text-slate-300/90 mt-0.5 leading-snug italic">{item.notes}</div>}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <button
                  onClick={() => onCycleStatus(item.id)}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-300 hover:text-white transition-colors"
                  title="Click to cycle status"
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} />
                  {status.label}
                </button>
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-300 hover:text-white transition-colors"
                    title={item.link}
                  >
                    <ExternalLink size={10} /> {prettyHost(item.link)}
                  </a>
                )}
                {item.location && (
                  <a
                    href={googleMapsDirectionsUrl(item.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-300 hover:text-white transition-colors"
                    title={`Directions to ${item.location}`}
                  >
                    <Navigation size={10} /> Directions
                  </a>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
              <button onClick={() => onEdit(item)} className="p-1 rounded hover:bg-white/10 text-slate-200" title="Edit">
                <Edit3 size={13} />
              </button>
              <button onClick={() => onDelete(item.id)} className="p-1 rounded hover:bg-red-500/20 text-red-300" title="Delete">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group relative rounded-xl border bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm hover:shadow-md transition-all overflow-hidden"
      style={{ borderColor: `${cat.colour}30` }}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{
          background: rangeMarker === "middle"
            ? `repeating-linear-gradient(to bottom, ${cat.colour} 0, ${cat.colour} 4px, transparent 4px, transparent 7px)`
            : cat.colour,
        }}
      />
      <div className="pl-4 pr-3 py-2.5">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 flex-shrink-0">
            <Icon size={15} style={{ color: cat.colour }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <button onClick={() => onTitleClick?.(item)} className="font-medium text-slate-900 dark:text-slate-100 leading-tight text-left hover:underline">{item.title}</button>
              {spanLabel && (
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ background: `${cat.colour}15`, color: cat.colour }}
                >
                  {spanLabel}
                </span>
              )}
              {slot && slot.id !== "anytime" && !spanLabel && (
                <span className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-0.5">
                  <Clock size={10} /> {slot.label}
                </span>
              )}
            </div>
            {item.notes && <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-snug">{item.notes}</div>}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <button
                onClick={() => onCycleStatus(item.id)}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
                title="Click to cycle status"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} />
                {status.label}
              </button>
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-900 transition-colors"
                  style={{ color: cat.colour }}
                  title={item.link}
                >
                  <ExternalLink size={10} /> {prettyHost(item.link)}
                </a>
              )}
              {item.location && (
                <a
                  href={googleMapsDirectionsUrl(item.location)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-900 transition-colors"
                  title={`Directions to ${item.location}`}
                >
                  <Navigation size={10} /> Directions
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onEdit(item)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400" title="Edit">
              <Edit3 size={13} />
            </button>
            <button onClick={() => onDelete(item.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500" title="Delete">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
